import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { DialogActions, DialogContent, Divider, Chip } from '@material-ui/core';
import PickupDialogActions from './PickupDialogActions';
import Select from '../../../../../common/StyledSelect';
import CustomTextField from '../common/CustomTextField';
import { MAX_LENGTH_CONSTRAINTS } from '../common/constants';
import appConstants from '../../../../../../appConstants';
import { formatAddress } from '../../../utils/utils';
import { getNoFreightPickupIds } from './util';

const styles = (theme) => ({
  header: {
    fontSize: theme.headerTextFontSize,
    fontWeight: 600,
    color: theme.iconColor,
    marginTop: 10
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 16
  },
  dialogActionsClass: {
    width: '100%',
    justifyContent: 'space-between'
  },
  stopDetailsContainer: {
    margin: '12px 0'
  },
  stopName: {
    color: theme.HeaderSelectedText,
    fontSize: theme.headerTextFontSize,
    fontWeight: 600
  },
  stopAddress: {
    color: theme.subHeaderTextColor,
    fontSize: theme.textFontSize,
    fontWeight: 400
  },
  itemContainer: {
    margin: '10px 0px'
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.disableButtonText,
    fontWeight: 600
  },
  itemSubHeader: {
    fontSize: theme.textFontSize,
    fontWeight: 400,
    color: theme.subHeaderTextColor,
    marginTop: 8,
    marginBottom: 8
  },
  contactNameField: { width: '100%', marginBottom: 10 }
});

const NoFreightReasonDialogContent = ({
  classes,
  editBillsDataArray,
  stopData,
  handleClose,
  pickupIdToNumberMap = {},
  noFreightCodes = [],
  enableNoFreightCode = false,
  enforceNoFreightReasonCodeIfExists = false,
  handleNoFreightReasonSubmit
}) => {
  const [noFreightShipmentReasons, setNoFreightShipmentReasons] = useState([]);
  const [showNoFreightCodeDropdown, setShowNoFreightCodeDropdown] = useState(false);

  useEffect(() => {
    const flag = enableNoFreightCode && noFreightCodes?.length > 0;
    setShowNoFreightCodeDropdown(flag);
  }, [enableNoFreightCode, noFreightCodes]);

  useEffect(() => {
    const noFreightPickupIds = getNoFreightPickupIds(editBillsDataArray);
    const noFreightShipmentReasons = getNoFreightShipmentReasons(noFreightPickupIds);
    setNoFreightShipmentReasons(noFreightShipmentReasons);
  }, [editBillsDataArray]);

  const getNoFreightShipmentReasons = (noFreightPickupIds) => {
    return noFreightPickupIds.map((pickupId) => ({
      pickupId,
      noFreightCode: null,
      contactName: null,
      errorInfo: {
        noFreightCode: null,
        contactName: null
      }
    }));
  };

  const handleChange = (field, index) => (value) => {
    const updatedNFShipmentReasons = [...noFreightShipmentReasons];
    if (field === 'noFreightCode') {
      value = value && value?.value;
    }
    updatedNFShipmentReasons[index][field] = value;
    updatedNFShipmentReasons[index].errorInfo[field] = null; //Clear the error
    setNoFreightShipmentReasons(updatedNFShipmentReasons);
  };

  const checkIfValid = () => {
    const NFReasonsCopy = [...noFreightShipmentReasons];
    let isValid = true;
    const shouldEnforceNoFreightReasonCode = showNoFreightCodeDropdown && enforceNoFreightReasonCodeIfExists;
    NFReasonsCopy.forEach((shipmentReason) => {
      if (shouldEnforceNoFreightReasonCode && !shipmentReason.noFreightCode) {
        shipmentReason.errorInfo.noFreightCode = '* Reason Code is required';
        isValid = false;
      }
      if (!shipmentReason.contactName) {
        shipmentReason.errorInfo.contactName = '* Customer Name is required';
        isValid = false;
      }
    });
    if (!isValid) {
      setNoFreightShipmentReasons(NFReasonsCopy);
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (!checkIfValid()) {
      return;
    }
    const noFreightPickups = noFreightShipmentReasons.map(
      ({ pickupId, noFreightCode, contactName }) => ({
        pickupId,
        noFreightCode,
        contactName
      })
    );
    handleNoFreightReasonSubmit(noFreightPickups);
  };

  return (
    <>
      <DialogContent className="customScroll">
        <StopDetails classes={classes} stopData={stopData} />
        <Divider />
        <div className={classes.header}>Incomplete Pickups</div>
        {noFreightShipmentReasons.map((shipmentReason, index) => (
          <NoFreightShipmentReasonItem
            key={shipmentReason.pickupId}
            shipmentReason={shipmentReason}
            index={index}
            pickupIdToNumberMap={pickupIdToNumberMap}
            classes={classes}
            noFreightCodes={noFreightCodes}
            handleChange={handleChange}
            showNoFreightCodeDropdown={showNoFreightCodeDropdown}
          />
        ))}
      </DialogContent>
      <div className={classes.dialogFooter}>
        <DialogActions className={classes.dialogActionsClass}>
          <PickupDialogActions handleSubmit={onSubmit} handleClose={handleClose} />
        </DialogActions>
      </div>
    </>
  );
};

export default withStyles(styles)(NoFreightReasonDialogContent);

const StopDetails = ({ classes, stopData }) => {
  return (
    <div className={classes.stopDetailsContainer}>
      <div className={classes.stopName}>{stopData.name}</div>
      <div className={classes.stopAddress}>
        {formatAddress({
          address: stopData?.address,
          address2: stopData?.address2,
          city: stopData?.city,
          state: stopData?.state,
          zip: stopData?.zip
        })}
      </div>
    </div>
  );
};

const NoFreightShipmentReasonItem = ({
  shipmentReason,
  index,
  pickupIdToNumberMap,
  classes,
  showNoFreightCodeDropdown,
  noFreightCodes = [],
  handleChange
}) => {
  const { pickupId, noFreightCode, contactName, errorInfo } = shipmentReason;
  const pickupNumber = pickupIdToNumberMap?.[pickupId] || pickupId;
  const PICKUP_STATUS = appConstants.PICKUP_STATUS.NO_FREIGHT;

  return (
    <div className={classes.itemContainer}>
      <div className={classes.itemHeader}>
        <span>{`PICKUP # ${pickupNumber}`}</span>
        <Chip size="small" label={PICKUP_STATUS} color="primary" variant="outlined" />
      </div>
      <div className={classes.itemSubHeader}>NO FREIGHT REASON</div>
      {showNoFreightCodeDropdown && (
        <Select
          textFieldProps={{
            id: `noFreightCode_${pickupId}`,
            label: 'Reason Code',
            variant: 'outlined',
            helperText: errorInfo?.noFreightCode
          }}
          value={noFreightCode || null}
          onChange={handleChange('noFreightCode', index)}
          error={Boolean(errorInfo?.noFreightCode)}
          data={noFreightCodes}
          createLabel="Select Reason Code"
        />
      )}
      <CustomTextField
        className={classes.contactNameField}
        id="contactName"
        handleChange={handleChange('contactName', index)}
        value={contactName || null}
        error={errorInfo?.contactName}
        inputProps={{
          maxLength: MAX_LENGTH_CONSTRAINTS.NO_FREIGHT_FORM.CUSTOMER_NAME,
          focused: true
        }}
        label="Customer (First and Last) Name"
      />
      <Divider />
    </div>
  );
};
